import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

// Interfaces
interface ModelState {
    loaded: boolean;
    loading: boolean;
    error: Error | null;
}

/**
 * ModelLoader class for handling GLTF model loading and management.
 * Provides methods to load models and extract specific models for different tile types.
 */
export class ModelLoader {
    private loader: GLTFLoader;
    private models: ModelState;
    private originalScene: THREE.Group | null;
    private modelsByName: Map<string, THREE.Mesh>;

    constructor() {
        this.loader = new GLTFLoader();
        this.models = {
            loaded: false,
            loading: false,
            error: null
        };

        // Store the original scene for potential future use
        this.originalScene = null;
        
        // Model name-to-object mapping for quick lookup
        this.modelsByName = new Map<string, THREE.Mesh>();
        this.loadModels();
        //console.log("ModelLoader initialized");
    }

    /**
     * Load GLTF models from the scene file
     * @returns Promise that resolves when models are loaded
     */
    loadModels(): Promise<ModelState> {
        if (this.models.loaded) {
            return Promise.resolve(this.models);
        }

        if (this.models.loading) {
            // Return a promise that resolves when loading is complete
            return new Promise((resolve, reject) => {
                const checkLoaded = () => {
                    if (this.models.loaded) {
                        resolve(this.models);
                    } else if (this.models.error) {
                        reject(this.models.error);
                    } else {
                        setTimeout(checkLoaded, 100);
                    }
                };
                checkLoaded();
            });
        }

        this.models.loading = true;

        return new Promise((resolve, reject) => {
            this.loader.load(
                // Resource URL
                'scene/scene.gltf',
                // Called when resource is loaded
                (gltf) => {
                    this.originalScene = gltf.scene;
                    //console.log("Scene loaded: ", this.originalScene, " gltf.scene: ", gltf.scene );
                    // Process all materials to ensure correct texture encoding
                    gltf.scene.traverse((node) => {
                        if (node instanceof THREE.Mesh && node.material) {
                            // Handle both single and multi-materials
                            const materials = Array.isArray(node.material) ? node.material : [node.material];
                            
                            materials.forEach((material: THREE.Material) => {
                                if ((material as any).map) {
                                    // Set texture encoding to LINEAR for proper brightness
                                    (material as any).map.encoding = THREE.LinearEncoding;
                                    (material as any).map.needsUpdate = true;
                                    material.needsUpdate = true;
                                }
                            });
                        }
                    });
                    // Extract and catalog all models from the scene
                    if (gltf.scene instanceof THREE.Group) {
                        this.extractAllModels(gltf.scene);
                    } else {
                        console.error("Error: gltf.scene is not an instance of THREE.Scene");
                    }
                    
                    this.models.loaded = true;
                    this.models.loading = false;
                    resolve(this.models);
                },
                // Called when loading is in progress
                (xhr) => {
                    const percentComplete = (xhr.loaded / xhr.total) * 100;
                },
                // Called when loading has errors
                (error) => {
                    console.error("Error loading GLTF models:", error);
                    this.models.error = error instanceof Error ? error : new Error(String(error));
                    this.models.loading = false;
                    reject(this.models.error);
                }
            );
        });
    }

    /**
     * Extract and catalog all models from the loaded scene
     * @param scene - The loaded GLTF scene
     */
    extractAllModels(scene: THREE.Group): void {
        // Get all mesh objects from the scene
        const meshes: THREE.Object3D[] = [];
        
        scene.traverse(node => {
            if (node instanceof THREE.Mesh) {
                meshes.push(node);
            }
        });
        
        console.log(`Found ${meshes.length} meshes in the scene`);
        
        if (meshes.length === 0) {
            console.error("No meshes found in the scene - models will not be available");
            return;
        }
        
        // Process each mesh and store it by name
        meshes.forEach(mesh => {
            // Store the original mesh in our catalog by name
            const name = mesh.name || `unnamed_${Math.random().toString(36).substring(2, 9)}`;
            
            try {
                // Make a clone that we'll prepare for use
                const preparedMesh = mesh.clone() as THREE.Mesh;
                
                // Rotate to face upright and adjust scale
                preparedMesh.rotation.set(-Math.PI/2, 0, 0);
                this.adjustModelScale(preparedMesh);
                
                // Store in the name-based lookup Map
                this.modelsByName.set(name, preparedMesh);
            } catch (error) {
                console.error(`Failed to process mesh: ${name}`, error);
            }
        });
        
        console.log(`Processed ${this.modelsByName.size} models`);
        //console.log("Available models:", Array.from(this.modelsByName.keys()).join(", "));
    }

    /**
     * Get a floor tile model appropriate for the given context
     * @returns The selected floor tile model
     */
    getFloorTileForContext(): THREE.Mesh | null {
        // TODO: Select appropriate floor tile model based on context
        return this.modelsByName.get("Dungeon_ground_tile4_DungeonProps_0") || null;
    }
    
    /**
     * Get a wall tile model appropriate for the given context
     * @returns The selected wall tile model
     */
    getWallTileForContext(): THREE.Mesh | null {
        // TODO: Select appropriate wall tile model
        return this.modelsByName.get("DungeonWall_mod1_DungeonProps_0") || null;
    }

    /**
     * Get a model by its name
     * @param name - The name of the model to retrieve
     * @returns The requested model or null if not found
     */
    getModelByName(name: string): THREE.Mesh | null {
        const model = this.modelsByName.get(name);
        
        if (!model) {
            console.warn(`Model not found: ${name}`);
            return null;
        }
        
        // Ensure we're returning a proper clone
        const clone = model.clone();
        
        // Deep clone materials to avoid shared material issues
        if (clone.material) {
            if (Array.isArray(clone.material)) {
                clone.material = clone.material.map(m => m.clone());
            } else {
                clone.material = clone.material.clone();
            }
        }
        
        return clone;
    }

    /**
     * Adjust the model scale to fit within a 1x1 tile
     * @param model - The model to adjust
     */
    adjustModelScale(model: THREE.Mesh): void {
        // Calculate the bounding box of the model
        const boundingBox = new THREE.Box3().setFromObject(model);
        const size = new THREE.Vector3();
        boundingBox.getSize(size);
        
        // Calculate scale factors to fit the model within a 1x1x1 cube
        // We want the model to cover the full tile area (1x1), but not exceed it
        const scaleX = 1 / size.x;
        const scaleZ = 1 / size.z;
        
        // Use the smaller scale factor to maintain proportions
        const scale = Math.min(scaleX, scaleZ);
        
        // Apply scaling
        model.scale.set(scale, scale, scale);

        // Adjust the height (Y position) so the model sits on the floor
        // Recalculate bounding box after scaling
        const newBoundingBox = new THREE.Box3().setFromObject(model);
        const newSize = new THREE.Vector3();
        newBoundingBox.getSize(newSize);
        
        if (model.geometry) {
            model.geometry.boundingBox = newBoundingBox;
            // Position the bottom of the model at Y=0
            const yOffset = newSize.y / 2;
            model.position.y = yOffset;
        }
    }

    /**
     * Get the default floor tile model
     * @returns Floor tile model or null if not loaded
     */
    getFloorTileModel(): THREE.Mesh | null {
        return this.modelsByName.get("Dungeon_ground_tile4_DungeonProps_0") || null;
    }

    /**
     * Get a template mesh directly (no clone) for InstancedMesh sharing
     * @param name - The name of the model to retrieve
     * @returns The template mesh or null if not found
     */
    getTemplateMesh(name: string): THREE.Mesh | null {
        return this.modelsByName.get(name) ?? null;
    }

    /**
     * Check if models are loaded
     * @returns True if models are loaded, false otherwise
     */
    isLoaded(): boolean {
        return this.models.loaded;
    }
}

// Create global instance
declare global {
    interface Window {
        modelLoader: ModelLoader;
    }
}

export const modelLoader = new ModelLoader();; 